import {
	OrderStatus,
	Severity,
	SystemAddOn,
	TurnaroundTime,
} from "node-library/dist/constants";
import {
	formatAbsoluteDate,
	formatCustomDate,
} from "node-library/dist/helpers/date_helper";
import {
	FrontendCompanyTestCategory,
	FrontendCompanyWithAdditionalInfomation,
	FrontendOrder,
	FrontendSampleInstance,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendSampleTestInformation,
	FrontendUserInfo,
} from "node-library/dist/portal";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import SamplePreviewTable from "../../newOrder/samplePreviewTable";

import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import PrintIcon from "@mui/icons-material/Print";
import { Card, Divider } from "@mui/material";
import { NeutralColor, PrimaryColor } from "node-library/dist/colors";
import { doesCompanyHaveAddonsEnabled } from "node-library/dist/helpers/request_helper";
import {
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";
import {
	BlackButton,
	CustomIconButton,
	DropdownButton,
	GreenButton,
} from "../../../../common-components/button";
import { NavBar } from "../../../../common-components/navbar";
import {
	COAIcon,
	CopyIcon,
	DownloadIcon,
	EditIcon,
	InvoiceIcon,
	UploadIcon,
	UserIcon,
} from "../../../../common-components/svgIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { getContentContainer } from "../../../../helpers/containerHelper";
import {
	formatDataForGoogleSheets,
	formatInstanceExcelData,
} from "../../../../helpers/googleSheetHelper";
import {
	getStatusColors,
	getStatusDescriptionText,
	getStatusString,
} from "../../../../helpers/orderHelper";
import {
	getShortTurnaroundTime,
	getTurnaroundTimeBackgroundColor,
} from "../../../../helpers/sampleHelper";
import { hasLabelPackageMatchingSampleInstance } from "../../../../helpers/testLabelHelper";
import EditOrderStateTimeModal from "../admin_view/editOrderStateTimeModal";
import PrintOrderLabelContent from "./printOrderLabelContent";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	orderTestCategory: FrontendCompanyTestCategory;
	order: FrontendOrder;
	user: FrontendUserInfo | undefined;

	sampleInstances: Array<FrontendSampleInstance>;

	goToEditOrderPage: (order: FrontendOrder) => void;
	goBackToPreviousPage: (reload: boolean) => void;
	deleteOrder: (order: FrontendOrder) => Promise<void>;
	viewOrderCOC: (order: FrontendOrder) => void;

	goToOrderReportPage: (order: FrontendOrder) => void;

	setDiscardOrderConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
	refreshSelectedOrder?: () => Promise<void>;
	setProceedAction: React.Dispatch<React.SetStateAction<() => void>>;
	specDirectories: Array<FrontendSampleSpecificationDirectory>;
	updateSpecification: (spec: FrontendSampleSpecification) => void;
}

export default function OrderDetailsContent(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		company,
		setCompany,
		orderTestCategory,
		order,
		goBackToPreviousPage,
		goToOrderReportPage,
		user,
		sampleInstances,
		viewOrderCOC,
		deleteOrder,
		setDiscardOrderConfirmOpen,
		setProceedAction,
		specDirectories,
		updateSpecification,
	} = props;

	//order updatable fields
	const [orderStatus, setOrderStatus] = useState<OrderStatus>(
		order.orderStatus,
	);

	const [registerTime, setRegisterTime] = useState<Date | undefined>(
		order.inserted ? new Date(order.inserted) : undefined,
	);

	const [completeTime, setCompleteTime] = useState<Date | undefined>(
		order.completeTime ? new Date(order.completeTime) : undefined,
	);

	const [cocPreviewUrl, setCocPreviewUrl] = useState<string>();

	const [editStateTimeModalOpen, setEditStateTimeModalOpen] =
		useState<boolean>(false);

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

	const TATExpectedCompletionTimeMap: Map<TurnaroundTime, Date> = new Map();

	const hasLabelsToPrint: boolean = hasLabelPackageMatchingSampleInstance(
		company.companyTestLabels,
		sampleInstances,
	);

	sampleInstances.forEach((instance) => {
		const tat: TurnaroundTime = (
			instance.sampleTestInfo as FrontendSampleTestInformation
		).turnaroundTime;
		const expectedCompleteTime: Date | undefined =
			instance.expectedCompletionTime;
		if (expectedCompleteTime) {
			if (TATExpectedCompletionTimeMap.has(tat)) {
				if (
					expectedCompleteTime > (TATExpectedCompletionTimeMap.get(tat) as Date)
				) {
					TATExpectedCompletionTimeMap.set(tat, expectedCompleteTime);
				}
			} else {
				TATExpectedCompletionTimeMap.set(tat, expectedCompleteTime);
			}
		}
	});

	const doesUserHaveDeleteAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.orders.delete,
			)
		: false;

	const doesUserHaveUpdateStatusAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.orders.updateStatus,
			)
		: false;
	const doesUserHaveLabelAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.testLabels.list,
			)
		: false;

	// const isInAdminView: boolean = checkUserTypeMeetsRequirement(
	// 	user?.role as SystemRole,
	// 	SystemRole.ADMIN
	// );

	let statusString: string;
	let statusColor: {
		backgroundColor: string;
		borderColor: string;
		textColor: string;
	};
	let statusDescriptionString: string;

	statusString = getStatusString(orderStatus, translation);
	statusDescriptionString = getStatusDescriptionText(orderStatus, translation);
	statusColor = getStatusColors(orderStatus);

	//dates
	// const submissionDateStr: string = order.submitted
	// 	? formatCustomDate(order.submitted)
	// 	: "";
	const packageReceivedDateStr: string = registerTime
		? formatCustomDate(registerTime)
		: "";

	const navbarItems: Array<{
		label: string;
		onClick: () => void;
		isCurrentItem: boolean;
	}> = [
		{
			label: translation("Orders"),
			onClick: () => goBackToPreviousPage(false),
			isCurrentItem: false,
		},
		{
			label: translation("OrderDetails"),
			onClick: () => {},
			isCurrentItem: true,
		},
	];

	let actionButtonOptions: Array<{
		key: string;
		onClick: () => void;
		icon: React.ReactNode;
		text: string;
		widthToShow?: number;
	}> = [];
	let keyActionOption: {
		key: string;
		onClick: () => void;
		children: any;
		disabled?: boolean;
	} = {
		key: "edit-order",
		onClick: () => {
			props.goToEditOrderPage(order);
		},
		children: translation("Editorder"),
	};

	useEffect(() => {
		setOrderStatus(order.orderStatus);
		setRegisterTime(order.inserted ? new Date(order.inserted) : undefined);
		setCompleteTime(
			order.completeTime ? new Date(order.completeTime) : undefined,
		);
	}, [order]);

	async function copyInstanceExportData() {
		const excelData = formatInstanceExcelData(
			order,
			company.testResultAndExpectationFields,
			orderTestCategory,
			sampleInstances,
			translation,
		);
		const formattedData = formatDataForGoogleSheets(excelData);

		await navigator.clipboard.writeText(formattedData);

		openSnackbar(
			translation("CopySuccessMsg"),
			Severity.SUCCESS,
			undefined,
			<GreenTickIcon />,
		);
	}

	switch (orderStatus) {
		case OrderStatus.IN_PROGRESS:
		case OrderStatus.COMPLETE:
			actionButtonOptions = [
				...[
					{
						key: "view-coc",
						onClick: () => {
							viewOrderCOC(order);
						},
						widthToShow: 780,
						icon: <COAIcon />,

						text: translation("ChainOfCustody"),
					},
					{
						key: "copy-instance-export-data",
						onClick: () => {
							copyInstanceExportData();
						},
						icon: <CopyIcon size={20} />,

						text: translation("Copyexportdata"),
					},
				],
				...(doesUserHaveDeleteAccess
					? [
							{
								key: "discard-order",
								onClick: () => {
									setProceedAction(() => () => deleteOrder(order));
									setDiscardOrderConfirmOpen(true);
								},
								icon: <ClearIcon sx={{ fontSize: "20px" }} />,
								text: translation("Discardorder"),
							},
							{
								key: "view-add-reports",
								onClick: () => {
									goToOrderReportPage(order);
								},
								icon: <UploadIcon size={20} />,

								text: translation("Uploadreports"),
							},
						]
					: []),
				...(doesUserHaveLabelAccess &&
				doesCompanyHaveAddonsEnabled(company, [
					SystemAddOn.TEST_LABEL_PRINTING,
				]) &&
				company.companyTestLabels &&
				company.companyTestLabels.length > 0 &&
				hasLabelsToPrint
					? [
							{
								key: "print-labels",
								onClick: () => {
									setCurrentPageIndex(1);
								},
								icon: <PrintIcon sx={{ fontSize: "20px" }} />,
								text: translation("PrintLabels"),
							},
						]
					: []),
			];
			break;
	}

	let actionButton: React.ReactNode = <></>;
	if (actionButtonOptions.length == 1) {
		actionButton = (
			<BlackButton
				id={"order-details-actionbutton"}
				buttonClassName={
					actionButtonOptions[0].widthToShow
						? "order-details-inprogress-action-button"
						: ""
				}
				onClick={(e) => {
					actionButtonOptions[0].onClick();
				}}
			>
				{actionButtonOptions[0].text}
			</BlackButton>
		);
	} else if (actionButtonOptions.length > 1) {
		actionButton = (
			<DropdownButton
				buttonColor="black"
				id={"order-details-actionbutton"}
				container={getContentContainer()}
				menuOptions={actionButtonOptions.map((option) => {
					return {
						...option,
						children: (
							<div className="icon-dropdown-menu-item">
								{option.icon}
								{option.text}
							</div>
						),
					};
				})}
				onClick={(e) => {}}
			>
				{translation("Actions")}
			</DropdownButton>
		);
	}

	const buttonPanel: React.ReactNode = (
		<div
			style={{
				display: "flex",
				gap: "5px",
				justifyContent: "center",
				flexWrap: "wrap",
			}}
		>
			{actionButton}

			<GreenButton
				id={"order-details-key-action-button"}
				onClick={keyActionOption.onClick}
				disabled={keyActionOption.disabled}
			>
				{keyActionOption.children}
			</GreenButton>
		</div>
	);
	const orderDetailsHeaderRow: React.ReactNode = (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: "10px",
				flexWrap: "wrap",
			}}
		>
			<div>
				<div style={{ marginLeft: "35px" }}>
					<NavBar items={navbarItems}></NavBar>
				</div>

				<div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
					<CustomIconButton
						id="order-details-back-button"
						size="small"
						onClick={() => {
							goBackToPreviousPage(false);
						}}
						icon={<KeyboardArrowLeftIcon />}
					></CustomIconButton>

					<div className="typography-heading-01">
						{`${translation("Order")} ${order.orderBarcodeId} - ${
							orderTestCategory.testCategoryName
						}`}
					</div>
				</div>
			</div>
			{buttonPanel}
		</div>
	);

	let licenseInfo: React.ReactNode;

	licenseInfo = (
		<>
			{" "}
			{order.licenseNumber || translation("None")}{" "}
			{order.licenseDesignation && order.licenseType && (
				<div>
					{`${translation(order.licenseType)} | ${translation(
						order.licenseDesignation,
					)}`}
				</div>
			)}
		</>
	);

	const orderDetailsCardRow: React.ReactNode = (
		<div className="order-details-card-row">
			<Card className="card-border-radius-14p card-medium-padding">
				<div className="order-details-header-card-content">
					<div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								gap: "10px",
							}}
						>
							<div
								className="typography-body-01 typography-bold admin-sample-status-box"
								style={{
									backgroundColor: statusColor.backgroundColor,
									border: `1px solid ${statusColor.borderColor}`,
									color: statusColor.textColor,
									minWidth: "20%",
								}}
							>
								{statusString}
							</div>
							{doesUserHaveUpdateStatusAccess && (
								<div
									style={{ cursor: "pointer" }}
									onClick={() => {
										setEditStateTimeModalOpen(true);
									}}
								>
									<EditIcon strokeColor={NeutralColor.NEUTRAL_GREY_950} />
								</div>
							)}
						</div>
						<div
							className="typography-caption"
							style={{ color: NeutralColor.NEUTRAL_GREY_500, marginTop: "5px" }}
						>
							{statusDescriptionString}
						</div>
					</div>
					<div>
						<div className="order-details-time-grid">
							{/* <div className="order-details-time-grid-item">
								<div
									className="typography-caption"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{translation("OrderSubmittedOn")}
								</div>
								<div className="typography-body-01 typography-bold">
									{submissionDateStr ? submissionDateStr.split(" | ")[0] : "--"}
								</div>
								<div className="typography-caption">
									{submissionDateStr ? submissionDateStr.split(" | ")[1] : ""}
								</div>
							</div>
							<Divider orientation="vertical" flexItem></Divider> */}

							<div className="order-details-time-grid-item">
								<div
									className="typography-caption"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{translation("PackageReceivedOn")}
								</div>
								<div className="typography-body-01 typography-bold">
									{packageReceivedDateStr
										? packageReceivedDateStr.split(" | ")[0]
										: "--"}
								</div>
								<div className="typography-caption">
									{packageReceivedDateStr
										? packageReceivedDateStr.split(" | ")[1]
										: ""}
								</div>
							</div>
							<Divider orientation="vertical" flexItem></Divider>

							<div className="order-details-time-grid-item">
								<div
									className="typography-caption"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{translation("CompletedOn")}
								</div>
								<div
									className="typography-body-01 typography-bold"
									style={{
										color: completeTime ? PrimaryColor.PRIMARY_500 : "black",
									}}
								>
									{completeTime
										? formatCustomDate(completeTime).split(" | ")[0]
										: "--"}
								</div>
							</div>
						</div>
						<Divider orientation="horizontal" flexItem></Divider>
						<div>
							<div className="order-details-time-grid-item">
								<div
									className="typography-caption"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{translation("EstimatedCompletionDate")}
								</div>
								<div className="order-details-eta-time-grid">
									{TATExpectedCompletionTimeMap.size > 0 ? (
										Array.from(TATExpectedCompletionTimeMap).map(
											([key, value]) => {
												const TATBackgroundColor: string =
													getTurnaroundTimeBackgroundColor(key);

												return (
													<div key={key}>
														<div
															className="typography-caption turnaround-time-box"
															style={{
																backgroundColor: TATBackgroundColor,
																width: "fit-content",
															}}
														>
															{getShortTurnaroundTime(key, translation)}
														</div>
														<div className="typography-body-01">
															{formatAbsoluteDate(value)}
														</div>
													</div>
												);
											},
										)
									) : (
										<div
											className="typography-body-01 typography-bold"
											style={{ marginTop: "5px" }}
										>
											{"--"}
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</Card>
			<Card className="card-border-radius-14p card-medium-padding">
				<div className="order-details-client-billing-card-content">
					<div>
						<div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
							<div className="order-details-header-icon-container">
								<UserIcon
									strokeColor={PrimaryColor.PRIMARY_500}
									fillColor={PrimaryColor.PRIMARY_500}
								></UserIcon>
							</div>
							<div className="typography-body-01 typography-bold">
								{translation("ClientDetails")}
							</div>
						</div>

						<div
							style={{
								marginTop: "30px",
								display: "flex",
								flexDirection: "column",
								gap: "10px",
							}}
						>
							<div className="order-details-user-info-row typography-body-02">
								<div className="order-details-user-info-row-title">
									{translation("Name")}
								</div>
								<div className="order-details-info-row-info">
									{order.orderedBy}
								</div>
							</div>
							<div className="order-details-user-info-row typography-body-02">
								<div className="order-details-user-info-row-title">
									{translation("Email")}
								</div>
								<div className="order-details-info-row-info">{order.email}</div>
							</div>
							<div className="order-details-user-info-row typography-body-02">
								<div className="order-details-user-info-row-title">
									{translation("Phone")}
								</div>
								<div className="order-details-info-row-info">
									{order.phoneNumber}
								</div>
							</div>
							<div className="order-details-user-info-row typography-body-02">
								<div className="order-details-user-info-row-title">
									{translation("License")}
								</div>
								<div className="order-details-info-row-info">{licenseInfo}</div>
							</div>

							<div className="order-details-user-info-row typography-body-02">
								<div className="order-details-user-info-row-title">
									{translation("SendReportCopiesTo")}
								</div>
								<div className="order-details-info-row-info">
									{order.testReportCopyEmails &&
									order.testReportCopyEmails.length > 0
										? order.testReportCopyEmails.map((email) => {
												return <div key={email}> {`${email}`}</div>;
											})
										: translation("None")}
								</div>
							</div>
						</div>
					</div>

					<Divider orientation="vertical" flexItem></Divider>
					<div>
						<div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
							<div className="order-details-header-icon-container">
								<InvoiceIcon></InvoiceIcon>
							</div>
							<div className="typography-body-01 typography-bold">
								{translation("BillingDetails")}
							</div>
						</div>

						<div
							style={{
								marginTop: "30px",
								display: "flex",
								flexDirection: "column",
								gap: "10px",
							}}
						>
							<div className="order-details-user-info-row typography-body-02">
								<div className="order-details-user-info-row-title">
									{translation("Name")}
								</div>
								<div className="order-details-info-row-info">
									{order.billingName}
								</div>
							</div>
							<div className="order-details-user-info-row typography-body-02">
								<div className="order-details-user-info-row-title">
									{translation("Email")}
								</div>
								<div className="order-details-info-row-info">
									{order.billingEmail}
								</div>
							</div>
							<div className="order-details-user-info-row typography-body-02">
								<div className="order-details-user-info-row-title">
									{translation("Phone")}
								</div>
								<div className="order-details-info-row-info">
									{order.billingPhoneNumber}
								</div>
							</div>
							<div className="order-details-user-info-row typography-body-02">
								<div className="order-details-user-info-row-title">
									{translation("Address")}
								</div>
								<div className="order-details-info-row-info">
									<div>{`${order?.billingAddress}, `}</div>
									<div>{`${order?.billingCity}, ${order?.billingProvince}, ${order?.billingCountry} `}</div>
									<div>{order?.billingPostalCode}</div>
								</div>
							</div>

							<div className="order-details-user-info-row typography-body-02">
								<div className="order-details-user-info-row-title">
									{translation("SendBillingCopiesTo")}
								</div>
								<div className="order-details-info-row-info">
									{order.billingInfoCopyEmails &&
									order.billingInfoCopyEmails.length > 0
										? order.billingInfoCopyEmails.map((email) => {
												return <div key={email}> {`${email}`}</div>;
											})
										: translation("None")}
								</div>
							</div>
						</div>
					</div>
				</div>
			</Card>
		</div>
	);

	let viewSampleTable: JSX.Element = (
		<SamplePreviewTable
			company={company}
			setCompany={setCompany}
			user={user as FrontendUserInfo}
			sampleInstances={sampleInstances}
			isReadOnlyTable={true}
			isDetailsPageTable={true}
			goToUpdateSample={() => {}}
			deleteSample={() => {}}
			duplicateSample={() => {}}
			openEditExpectationModal={() => {}}
			orderTestCategory={orderTestCategory}
			specDirectories={specDirectories}
			updateSpecification={updateSpecification}
		/>
	);

	const cocCard: React.ReactNode = (
		<Card
			className="card-border-radius-14p card-medium-padding order-details-coa-card"
			style={{ cursor: "pointer" }}
			onClick={() => {
				viewOrderCOC(order);
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					gap: "5px",
					alignItems: "center",
				}}
			>
				<div className="typography-body-01">
					{translation("ChainOfCustody")}
				</div>
				<DownloadIcon />
			</div>
			{cocPreviewUrl && (
				<img
					style={{ width: "100%", height: "auto", marginTop: "10px" }}
					src={`${cocPreviewUrl}`}
					alt="COC Preview Image"
				></img>
			)}
		</Card>
	);

	const sampleDetailsBody: React.ReactNode = (
		<div className="order-details-body">
			{viewSampleTable}
			{cocCard}
		</div>
	);

	let currentComponent: React.ReactNode;

	switch (currentPageIndex) {
		case 0:
			currentComponent = (
				<div>
					{orderDetailsHeaderRow}
					{orderDetailsCardRow}

					{sampleDetailsBody}

					<EditOrderStateTimeModal
						orderId={order.orderId}
						open={editStateTimeModalOpen}
						setOpen={setEditStateTimeModalOpen}
						orderStatus={orderStatus}
						setOrderStatus={setOrderStatus}
						completeTime={completeTime}
						setCompleteTime={setCompleteTime}
					></EditOrderStateTimeModal>
				</div>
			);
			break;
		case 1:
			currentComponent = (
				<PrintOrderLabelContent
					company={company}
					setCompany={setCompany}
					orderTestCategory={orderTestCategory}
					order={order}
					user={user}
					sampleInstances={sampleInstances}
					goBackToPreviousPage={() => {
						setCurrentPageIndex(0);
					}}
					goBackToOrderPage={() => goBackToPreviousPage(false)}
					specDirectories={specDirectories}
					updateSpecification={updateSpecification}
				></PrintOrderLabelContent>
			);
			break;
		default:
			currentComponent = <></>;
	}

	return <div style={{ margin: "0 auto" }}>{currentComponent}</div>;
}
